import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

export default function PrivacyPolicyPage() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - FuturePath Technologies</title>
        <meta name="description" content="Privacy Policy for FuturePath Technologies - how we collect, use, and protect your information." />
      </Helmet>

      <section className="relative min-h-[30vh] flex items-center justify-center pt-32 pb-12">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-transparent opacity-50" />
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold font-outfit mb-4 text-white">
              Privacy Policy
            </h1>
            <p className="text-muted-foreground">
              Last updated: August 4, 2026
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="prose prose-invert max-w-none space-y-10"
          >

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">1. Introduction</h2>
              <p className="text-muted-foreground leading-relaxed">
                FuturePath Technologies ("we," "us," or "our") respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website (futurepathtechnologies.com) or engage our services.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">2. Information We Collect</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">We may collect the following types of information:</p>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li><strong className="text-white">Information you provide directly:</strong> name, email address, phone number, company name, and project details submitted through our consultation or contact forms.</li>
                <li><strong className="text-white">Automatically collected information:</strong> IP address, browser type, device information, pages visited, and time spent on our site, collected via analytics tools (e.g., Google Analytics).</li>
                <li><strong className="text-white">Cookies:</strong> small data files stored on your device to improve site functionality and understand how visitors use our site. You can disable cookies through your browser settings.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">3. How We Use Your Information</h2>
              <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
                <li>To respond to consultation requests and inquiries</li>
                <li>To provide, operate, and improve our services</li>
                <li>To send project-related communications</li>
                <li>To understand website usage and improve user experience</li>
                <li>To comply with legal obligations</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-3">
                We do not sell your personal information to third parties.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">4. How We Share Your Information</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may share information with trusted third-party service providers who help us operate our website and business (e.g., hosting providers, analytics tools, email services), solely for the purposes described in this policy. We may also disclose information if required by law or to protect our legal rights.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">5. Data Retention</h2>
              <p className="text-muted-foreground leading-relaxed">
                We retain personal information only for as long as necessary to fulfill the purposes described in this policy, or as required by applicable law.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">6. Your Rights</h2>
              <p className="text-muted-foreground leading-relaxed">
                Depending on your location, you may have rights to access, correct, delete, or restrict the use of your personal information. To exercise these rights, contact us using the details below.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">7. Data Security</h2>
              <p className="text-muted-foreground leading-relaxed">
                We use reasonable administrative and technical safeguards to protect your information. However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">8. Third-Party Links</h2>
              <p className="text-muted-foreground leading-relaxed">
                Our website may contain links to third-party sites. We are not responsible for the privacy practices or content of those sites.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">9. Children's Privacy</h2>
              <p className="text-muted-foreground leading-relaxed">
                Our services are not directed to individuals under 18. We do not knowingly collect personal information from children.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">10. Changes to This Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last updated" date.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">11. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have questions about this Privacy Policy, please contact us at: <br />
                <span className="text-white">[your business email]</span>
              </p>
            </div>

          </motion.div>
        </div>
      </section>
    </>
  );
}
